import * as React from 'react';
import { useEffect, useRef, useState } from 'react';
import _ from 'lodash';
import useFetch from 'use-http';
import { useForm } from 'react-hook-form';
import Dialog from 'src/components/common/Dialog';
import Button from 'src/components/controls/forms/redesign/FormButton';
import RHFInputBox from 'src/components/controls/tempRHFControls/RHFInputBox';
import TimeEmpCutoffItem from 'src/models/personnel/TimeEmpCutoffItem';

interface ITimeEmpCutoffDialogProps {
    dialogName: string;
    hideDialog: () => void;
    items: Array<TimeEmpCutoffItem>;
    saving: boolean;
    onSave: (items: Array<TimeEmpCutoffItem>) => void;
}

const formatDate = (value: Date | string): string => {
    if (!value) {
        return '';
    }
    const date = new Date(value);
    return isNaN(date.getTime()) ? '' : date.toLocaleDateString();
};

// <input type="date"> needs YYYY-MM-DD - built from the Y/M/D parts directly (not toISOString(),
// which shifts by the viewer's UTC offset) same as other date fields elsewhere in this app.
const toDateInputValue = (value: Date | string): string => {
    if (!value) {
        return '';
    }
    const date = new Date(value);
    if (isNaN(date.getTime())) {
        return '';
    }
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    return `${date.getFullYear()}-${month}-${day}`;
};

// Mirrors the Time_Emp_Cutoff block (Period No./Begin Date/End Date/Cutoff Date) - PBEG_DATE/
// PEND_DATE are read-only (the pay period's own real dates, for context); only the Cutoff Date
// (END_DATE) is editable. Per the same simplification CPI Special got, the real screen's single
// "commit_data then close" button is replaced with isDirty toggling Close/Save Changes, and Save
// Changes only sends rows whose Cutoff Date actually changed (not the whole visible list) - see
// getChangedRows.
const TimeEmpCutoffDialog = (props: ITimeEmpCutoffDialogProps) => {
    const { register, getValues, formState } = useForm();
    const { get } = useFetch();
    const [items, setItems] = useState<Array<TimeEmpCutoffItem>>([]);
    const [rowsVersion, setRowsVersion] = useState<number>(0);

    useEffect(() => {
        setItems(props.items ?? []);
        setRowsVersion((v) => v + 1);
    }, [props.items]);

    const fetchItems = async (searchTerm?: string) => {
        const query = searchTerm ? `?search=${encodeURIComponent(searchTerm)}` : '';
        const result = await get(`/v1.0/Payroll/payhistory/timeempcutoff${query}`);
        if (Array.isArray(result?.items)) {
            setItems(result.items);
            setRowsVersion((v) => v + 1);
        }
    };

    const searchRef = useRef<(term: string) => void>();
    searchRef.current = (term: string) => fetchItems(term);
    const [debouncedSearch] = useState(() => _.debounce((term: string) => {
        searchRef.current(term);
    }, 500));

    const handleSearchChange = () => {
        const term = (getValues().periodSearch || '').replace(/-/g, '');
        if (term) {
            debouncedSearch(term);
        } else {
            debouncedSearch.cancel();
            fetchItems();
        }
    };

    const isDirty = formState.isDirty;

    // Only the rows whose Cutoff Date actually changed from what was loaded - Save Changes
    // shouldn't touch every currently-visible period's cutoff just because the dialog was opened.
    const getChangedRows = (): Array<TimeEmpCutoffItem> => {
        const values = getValues();
        const changed: Array<TimeEmpCutoffItem> = [];
        items.forEach((item, index) => {
            const rawValue = values?.items?.[index]?.endDate;
            if (!rawValue) {
                return;
            }
            const newDate = new Date(rawValue);
            const originalDate = item.endDate ? new Date(toDateInputValue(item.endDate)) : undefined;
            if (!originalDate || newDate.getTime() !== originalDate.getTime()) {
                changed.push({ periodNo: item.periodNo, endDate: newDate });
            }
        });
        return changed;
    };

    const handleLeftButtonClick = () => {
        if (!isDirty) {
            props.hideDialog();
            return;
        }
        props.onSave(getChangedRows());
    };

    return (
        <Dialog title="Set Cutoff EMail Date" dialogName={props.dialogName} isExitButtonInvisible={true} bodyStyle={{ width: '45%' }}>
            <div className="form-row" style={{ marginBottom: '.5rem' }}>
                <RHFInputBox
                    name="periodSearch"
                    type="text"
                    placeholder="Search Period No."
                    register={register}
                    onChange={handleSearchChange}
                    style={{ width: '100%' }}
                    bodyStyle={{ width: '14rem' }}
                />
            </div>
            <div style={{ overflowY: 'auto', maxHeight: '24rem' }}>
                <table style={{ width: '100%' }} key={rowsVersion}>
                    <thead>
                        <tr>
                            <th>Period No.</th>
                            <th>Begin Date</th>
                            <th>End Date</th>
                            <th>Cutoff Date</th>
                        </tr>
                    </thead>
                    <tbody>
                        {items.map((item, index) => (
                            <tr key={item.periodNo} style={{ textAlign: 'center' }}>
                                <td>{item.periodNo}</td>
                                <td>{formatDate(item.pBegDate)}</td>
                                <td>{formatDate(item.pEndDate)}</td>
                                <td>
                                    <RHFInputBox
                                        name={`items[${index}].endDate`}
                                        type="date"
                                        register={register}
                                        defaultVal={toDateInputValue(item.endDate)}
                                        style={{ width: '100%' }}
                                        bodyStyle={{ width: '100%' }}
                                    />
                                </td>
                            </tr>
                        ))}
                        {items.length === 0 && (
                            <tr>
                                <td colSpan={4} className="bold-centered">No Periods Found</td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
            <div className="form-row" style={{ marginTop: '1rem', justifyContent: 'center' }}>
                <Button
                    className="blue-hover-button"
                    style={{ width: 'auto' }}
                    text={props.saving ? 'Saving...' : isDirty ? 'Save Changes' : 'Close'}
                    disabled={props.saving}
                    onClick={handleLeftButtonClick}
                />
            </div>
        </Dialog>
    );
};

export default TimeEmpCutoffDialog;



// WEBPACK FOOTER //
// ./src/components/personnel/dialogs/employeePayroll/TimeEmpCutoffDialog.tsx